import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import config from "../config";
import placeimage from "../image/noimage.jpg";
import Faq from "./Faq";

export default function TrainerHomepage() {
    const navigate = useNavigate();
    const [searchCity, setSearchCity] = useState("");
    const [searchExpertise, setSearchExpertise] = useState("");
    const [trendingTrainers, setTrendingTrainers] = useState([]);

    useEffect(() => {
        fetchTrendingTrainers();
    }, []);

    const fetchTrendingTrainers = async () => {
        try {
            const response = await axios.get(`${config.apiUrl}/trainer`);
            setTrendingTrainers((response.data.data || []).slice(0, 8));
        } catch (error) {
            console.error("Error fetching trainers:", error);
        }
    };

    const handleSearch = () => {
        if (searchCity || searchExpertise) {
            let url = `/gym-fitness-trainer/${searchCity || 'all'}`;
            if (searchExpertise) {
                url += `?skill=${encodeURIComponent(searchExpertise)}`;
            }
            navigate(url);
        }
    };

    const imageOnError = (event) => {
        event.currentTarget.src = placeimage;
    };

    const categories = [
        { name: "Weight Loss", icon: "bi-graph-down-arrow", slug: "weight-loss", desc: "Fat burning & body transformation", color: "#ff6b35", bg: "#fff4f0" },
        { name: "Bodybuilding", icon: "bi-trophy-fill", slug: "bodybuilding", desc: "Muscle building & strength training", color: "#667eea", bg: "#f0f0ff" },
        { name: "Yoga & Meditation", icon: "bi-heart-fill", slug: "yoga", desc: "Mind-body balance & flexibility", color: "#11998e", bg: "#f0fdf9" },
        { name: "Zumba & Dance", icon: "bi-music-note-beamed", slug: "zumba", desc: "Fun cardio with energetic dance moves", color: "#d946a8", bg: "#fdf0f9" },
        { name: "CrossFit & HIIT", icon: "bi-lightning-fill", slug: "crossfit", desc: "High-intensity training for max results", color: "#f59e0b", bg: "#fffbeb" },
        { name: "Online Training", icon: "bi-laptop", slug: "online-training", desc: "Train from anywhere with virtual coaching", color: "#0ea5e9", bg: "#f0f9ff" },
        { name: "Diet & Nutrition", icon: "bi-apple", slug: "diet-nutrition", desc: "Personalized meal plans for your goals", color: "#16a34a", bg: "#f0fdf4" },
        { name: "Home Workout", icon: "bi-house-fill", slug: "home-workout", desc: "Effective workouts, zero equipment needed", color: "#8b5cf6", bg: "#f5f3ff" },
    ];

    const topCities = [
        "Delhi", "Mumbai", "Bengaluru", "Hyderabad", "Chennai", "Kolkata",
        "Pune", "Ahmedabad", "Jaipur", "Lucknow", "Chandigarh", "Indore",
        "New York", "London", "Dubai", "Singapore", "Sydney", "Toronto"
    ];

    const testimonials = [
        { name: "Rahul Sharma", role: "Client", text: "Lost 20kg in 6 months! The trainer I found here changed my life completely.", rating: 5, image: placeimage },
        { name: "Priya Verma", role: "Client", text: "Best platform to find certified trainers. The booking process is so easy!", rating: 5, image: placeimage },
        { name: "Amit Patel", role: "Trainer", text: "Got 50+ clients in just 2 months. This platform is a game-changer for trainers!", rating: 5, image: placeimage }
    ];

    const whyFeatures = [
        { icon: "bi-patch-check-fill", stat: "10,000+", title: "Verified Trainers", desc: "Every trainer undergoes rigorous background verification and certification checks before joining our platform.", badge: "100% Verified", color: "#3b82f6", iconBg: "#eff6ff" },
        { icon: "bi-globe-asia-australia", stat: "50+", title: "Cities Covered", desc: "Access certified trainers across India and internationally — choose in-person or convenient online sessions.", badge: "Global Reach", color: "#10b981", iconBg: "#f0fdf4" },
        { icon: "bi-star-half", stat: "4.8/5", title: "Average Rating", desc: "Read genuine feedback from verified clients only. Our review system ensures real experiences, every time.", badge: "Trusted Reviews", color: "#f59e0b", iconBg: "#fffbeb" },
        { icon: "bi-currency-rupee", stat: "₹500+", title: "Transparent Pricing", desc: "Compare trainer rates and packages openly. No hidden charges. Expert trainers starting from ₹500/session.", badge: "Best Value", color: "#8b5cf6", iconBg: "#f5f3ff" },
        { icon: "bi-lightning-charge-fill", stat: "< 2 min", title: "Instant Booking", desc: "Book sessions in real time and get trainer confirmation instantly via WhatsApp, email, or SMS.", badge: "Quick Response", color: "#ef4444", iconBg: "#fff1f2" },
        { icon: "bi-headset", stat: "24/7", title: "Customer Support", desc: "Our dedicated support team is available round-the-clock for bookings, queries, or any concerns.", badge: "Always Available", color: "#06b6d4", iconBg: "#ecfeff" },
    ];

    return (
        <>
            {/* SEO Meta Tags */}
            <Helmet>
                <title>Find the Best Fitness Trainers Near You or Online | MeraGym</title>
                <meta name="description" content="Connect with certified personal trainers worldwide. Find expert fitness coaches for weight loss, bodybuilding, yoga, and more. Book online or offline training sessions." />
                <meta name="keywords" content="personal trainer, fitness coach, online training, gym trainer, weight loss trainer, bodybuilding coach, yoga instructor, certified trainer" />
                <meta property="og:title" content="Find the Best Fitness Trainers Near You | MeraGym" />
                <meta property="og:description" content="Connect with certified personal trainers for weight loss, bodybuilding, yoga, and more. Book online or offline training sessions on MeraGym." />
                <meta property="og:type" content="website" />
                <meta property="og:url" content="https://meragym.com/gym-fitness-trainer" />
                <meta property="og:image" content="https://meragym.com/og-trainers.jpg" />
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content="Find Certified Fitness Trainers | MeraGym" />
                <meta name="twitter:description" content="Connect with expert fitness coaches for weight loss, bodybuilding, yoga and more." />
                <link rel="canonical" href="https://meragym.com/gym-fitness-trainer" />
            </Helmet>

            {/* A. Hero Section */}
            <section className="hero-section-trainer">
                <div className="hero-background-overlay"></div>
                <div className="container">
                    <div className="hero-content-center">
                        <h1 className="hero-main-heading">
                            Find the Best Fitness Trainers Near You or Online
                        </h1>
                        <p className="hero-subheading">
                            Connect with certified personal trainers worldwide
                        </p>

                        <div className="hero-search-bar">
                            <div className="search-input-group">
                                <i className="bi bi-geo-alt-fill search-icon"></i>
                                <input
                                    type="text"
                                    placeholder="Enter your city"
                                    value={searchCity}
                                    onChange={(e) => setSearchCity(e.target.value)}
                                    className="search-input city-input"
                                />
                            </div>
                            <div className="search-input-group">
                                <i className="bi bi-search search-icon"></i>
                                <input
                                    type="text"
                                    placeholder="Trainer expertise (e.g., Weight Loss)"
                                    value={searchExpertise}
                                    onChange={(e) => setSearchExpertise(e.target.value)}
                                    className="search-input expertise-input"
                                />
                            </div>
                            <button className="search-btn-hero" onClick={handleSearch}>
                                <i className="bi bi-search me-2"></i>
                                Search Trainers
                            </button>
                        </div>

                        <div className="hero-cta-buttons">
                            <Link to="/gym-fitness-trainer/all" className="btn-hero-primary">
                                <i className="bi bi-search me-2"></i>Find Trainers
                            </Link>
                            <Link to="/create-fitness-trainer-profile" className="btn-hero-secondary">
                                <i className="bi bi-person-plus me-2"></i>Register as Trainer
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* Sticky Search Bar (Mobile) */}
            <div className="sticky-search-mobile">
                <div className="container">
                    <div className="mobile-search-compact">
                        <input
                            type="text"
                            placeholder="Search trainers..."
                            value={searchCity}
                            onChange={(e) => setSearchCity(e.target.value)}
                            className="mobile-search-input"
                        />
                        <button className="mobile-search-btn" onClick={handleSearch}>
                            <i className="bi bi-search"></i>
                        </button>
                    </div>
                </div>
            </div>

            {/* C. Popular Trainer Categories — Redesigned */}
            <section className="trainer-cats-section">
                <div className="container">
                    <h2 className="section-title-center">Popular Trainer Categories</h2>
                    <p className="section-subtitle-center">Choose your fitness goal — find a specialist who delivers results</p>

                    <div className="tcat-grid">
                        {categories.map((cat, i) => (
                            <Link
                                key={i}
                                to={`/gym-fitness-trainer/all?skill=${cat.slug}`}
                                className="tcat-card"
                                style={{ "--tcat-color": cat.color, "--tcat-bg": cat.bg }}
                            >
                                <div className="tcat-icon-wrap">
                                    <i className={`bi ${cat.icon}`}></i>
                                </div>
                                <div className="tcat-body">
                                    <h3 className="tcat-name">{cat.name}</h3>
                                    <p className="tcat-desc">{cat.desc}</p>
                                </div>
                                <span className="tcat-arrow">
                                    <i className="bi bi-arrow-right-circle-fill"></i>
                                </span>
                            </Link>
                        ))}
                    </div>

                    <div className="tcs-footer">
                        <Link to="/gym-fitness-trainer/all" className="tcs-view-all-btn">
                            Browse All Trainer Specializations <i className="bi bi-arrow-right ms-2"></i>
                        </Link>
                    </div>
                </div>
            </section>

            {/* D. Trending Trainers */}
            <section className="trending-trainers-section">
                <div className="container">
                    <div className="section-header-row">
                        <div>
                            <h2 className="section-title-left">Trending Trainers</h2>
                            <p className="section-subtitle-left">Top-rated trainers with verified profiles</p>
                        </div>
                        <Link to="/gym-fitness-trainer/all" className="see-all-link">
                            View All <i className="bi bi-arrow-right-circle-fill ms-2"></i>
                        </Link>
                    </div>

                    <div className="trainers-grid">
                        {trendingTrainers.length > 0 ? (
                            trendingTrainers.map((trainer, index) => (
                                <div key={index} className="trainer-card-home">
                                    <div className="trainer-image-wrapper">
                                        <img
                                            src={trainer.photo}
                                            alt={trainer.name}
                                            onError={imageOnError}
                                            className="trainer-card-image"
                                        />
                                        <div className="verified-badge-home">
                                            <i className="bi bi-patch-check-fill"></i>
                                        </div>
                                    </div>
                                    <div className="trainer-card-content">
                                        <h3 className="trainer-name-home">{trainer.name}</h3>
                                        <p className="trainer-expertise-home">
                                            {trainer.skills?.[0]?.name || "Fitness Expert"}
                                        </p>
                                        <div className="trainer-location-home">
                                            <i className="bi bi-geo-alt-fill"></i>
                                            <span>{trainer.city}</span>
                                        </div>
                                        <Link
                                            to={`/gym-fitness-trainer/${trainer.city}/${trainer.slug}`}
                                            className="btn-view-profile-home"
                                        >
                                            View Profile
                                        </Link>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="col-12 text-center py-5">
                                <p className="text-muted">Loading trainers...</p>
                            </div>
                        )}
                    </div>
                </div>
            </section>

            {/* B. How It Works — moved after Trending Trainers */}
            <section className="how-it-works-section">
                <div className="container">
                    <h2 className="section-title-center">How It Works</h2>
                    <p className="section-subtitle-center">Find and book your perfect fitness trainer in 3 simple steps</p>

                    <div className="how-it-works-grid-enhanced">
                        <div className="work-step-card-enhanced">
                            <div className="step-number-badge">01</div>
                            <div className="step-icon-circle-enhanced search">
                                <i className="bi bi-search"></i>
                            </div>
                            <h3>Search & Discover</h3>
                            <p className="step-description">Find the perfect trainer for your fitness goals</p>
                            <ul className="step-features">
                                <li><i className="bi bi-check-circle-fill"></i> Search by city or location</li>
                                <li><i className="bi bi-check-circle-fill"></i> Filter by expertise & specialization</li>
                                <li><i className="bi bi-check-circle-fill"></i> Choose online or offline training</li>
                                <li><i className="bi bi-check-circle-fill"></i> Browse verified trainer profiles</li>
                            </ul>
                            <div className="step-arrow"><i className="bi bi-arrow-right"></i></div>
                        </div>

                        <div className="work-step-card-enhanced">
                            <div className="step-number-badge">02</div>
                            <div className="step-icon-circle-enhanced profile">
                                <i className="bi bi-person-badge"></i>
                            </div>
                            <h3>Review & Compare</h3>
                            <p className="step-description">Explore detailed profiles and make informed decisions</p>
                            <ul className="step-features">
                                <li><i className="bi bi-check-circle-fill"></i> View photos & transformation videos</li>
                                <li><i className="bi bi-check-circle-fill"></i> Check certifications & experience</li>
                                <li><i className="bi bi-check-circle-fill"></i> Read authentic client reviews</li>
                                <li><i className="bi bi-check-circle-fill"></i> Compare pricing & packages</li>
                            </ul>
                            <div className="step-arrow"><i className="bi bi-arrow-right"></i></div>
                        </div>

                        <div className="work-step-card-enhanced">
                            <div className="step-number-badge">03</div>
                            <div className="step-icon-circle-enhanced book">
                                <i className="bi bi-calendar-check"></i>
                            </div>
                            <h3>Book & Start Training</h3>
                            <p className="step-description">Connect instantly and begin your fitness journey</p>
                            <ul className="step-features">
                                <li><i className="bi bi-check-circle-fill"></i> Book sessions instantly</li>
                                <li><i className="bi bi-check-circle-fill"></i> Contact via WhatsApp or call</li>
                                <li><i className="bi bi-check-circle-fill"></i> Get instant confirmation</li>
                                <li><i className="bi bi-check-circle-fill"></i> Start your transformation today</li>
                            </ul>
                        </div>
                    </div>

                    <div className="how-it-works-cta">
                        <p className="cta-text">Ready to transform your fitness journey?</p>
                        <Link to="/gym-fitness-trainer/all" className="btn-start-now">
                            <i className="bi bi-rocket-takeoff me-2"></i>Get Started Now
                        </Link>
                    </div>
                </div>
            </section>

            {/* E. Top Cities */}
            <section className="top-cities-section">
                <div className="container">
                    <h2 className="section-title-center">Find Personal Trainers in Top Cities</h2>
                    <p className="section-subtitle-center">Connect with expert trainers in your city</p>

                    <div className="cities-grid">
                        {topCities.map((city, index) => (
                            <Link
                                key={index}
                                to={`/gym-fitness-trainer/${city.toLowerCase()}`}
                                className="city-card-link"
                            >
                                <div className="city-card-home">
                                    <i className="bi bi-geo-alt-fill city-icon"></i>
                                    <span>Personal Trainer in {city}</span>
                                    <i className="bi bi-arrow-right city-arrow"></i>
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            {/* F. Transformation Stories — hidden temporarily */}

            {/* G. Why Choose MeraGym Platform — Redesigned */}
            <section className="why-choose-trainer-section">
                <div className="container">
                    <h2 className="section-title-center">Why Choose MeraGym Platform</h2>
                    <p className="section-subtitle-center">India's #1 platform connecting fitness enthusiasts with certified trainers</p>

                    <div className="wct-trust-bar">
                        <div className="wct-trust-item">
                            <span className="wct-trust-num">10,000+</span>
                            <span className="wct-trust-label">Verified Trainers</span>
                        </div>
                        <div className="wct-trust-divider"></div>
                        <div className="wct-trust-item">
                            <span className="wct-trust-num">50+</span>
                            <span className="wct-trust-label">Cities</span>
                        </div>
                        <div className="wct-trust-divider"></div>
                        <div className="wct-trust-item">
                            <span className="wct-trust-num">4.8 ★</span>
                            <span className="wct-trust-label">Avg. Rating</span>
                        </div>
                        <div className="wct-trust-divider"></div>
                        <div className="wct-trust-item">
                            <span className="wct-trust-num">95%</span>
                            <span className="wct-trust-label">Success Rate</span>
                        </div>
                    </div>

                    <div className="wct-features-grid">
                        {whyFeatures.map((f, i) => (
                            <div key={i} className="wct-card" style={{ "--wct-color": f.color, "--wct-icon-bg": f.iconBg }}>
                                <div className="wct-card-header">
                                    <div className="wct-icon-wrap">
                                        <i className={`bi ${f.icon}`}></i>
                                    </div>
                                    <div className="wct-stat">{f.stat}</div>
                                </div>
                                <h3 className="wct-title">{f.title}</h3>
                                <p className="wct-desc">{f.desc}</p>
                                <span className="wct-badge">{f.badge}</span>
                            </div>
                        ))}
                    </div>

                    <div className="wct-bottom-cta">
                        <Link to="/gym-fitness-trainer/all" className="btn-wct-primary">
                            <i className="bi bi-search me-2"></i>Find Your Trainer Now
                        </Link>
                        <Link to="/create-fitness-trainer-profile" className="btn-wct-outline">
                            <i className="bi bi-person-plus me-2"></i>Join as Trainer
                        </Link>
                    </div>
                </div>
            </section>

            {/* H. Testimonials */}
            <section className="testimonials-section">
                <div className="container">
                    <h2 className="section-title-center">What Our Users Say</h2>
                    <p className="section-subtitle-center">Success stories from clients and trainers</p>

                    <div className="testimonials-grid">
                        {testimonials.map((testimonial, index) => (
                            <div key={index} className="testimonial-card-home">
                                <div className="testimonial-stars">
                                    {[...Array(testimonial.rating)].map((_, i) => (
                                        <i key={i} className="bi bi-star-fill"></i>
                                    ))}
                                </div>
                                <p className="testimonial-text">"{testimonial.text}"</p>
                                <div className="testimonial-author">
                                    <img src={testimonial.image} alt={testimonial.name} onError={imageOnError} className="author-image" />
                                    <div className="author-info">
                                        <h4>{testimonial.name}</h4>
                                        <p>{testimonial.role}</p>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* I. Call-to-Action Block */}
            <section className="trainer-cta-section">
                <div className="tcta-bg-shapes">
                    <div className="tcta-shape tcta-shape-1"></div>
                    <div className="tcta-shape tcta-shape-2"></div>
                    <div className="tcta-shape tcta-shape-3"></div>
                </div>
                <div className="container">
                    <div className="tcta-inner">
                        {/* Left: Text + CTA */}
                        <div className="tcta-left">
                            <span className="tcta-eyebrow">
                                <i className="bi bi-lightning-charge-fill me-1"></i> Join 10,000+ Trainers
                            </span>
                            <h2 className="tcta-heading">
                                Are you a<br />
                                <span className="tcta-highlight">Fitness Trainer?</span>
                            </h2>
                            <p className="tcta-subtext">
                                Create your free profile today and get discovered by thousands of clients actively looking for trainers like you.
                            </p>
                            <div className="tcta-perks">
                                <div className="tcta-perk">
                                    <i className="bi bi-check-circle-fill"></i>
                                    <span>Free to register — no hidden fees</span>
                                </div>
                                <div className="tcta-perk">
                                    <i className="bi bi-check-circle-fill"></i>
                                    <span>Get client enquiries directly on WhatsApp</span>
                                </div>
                                <div className="tcta-perk">
                                    <i className="bi bi-check-circle-fill"></i>
                                    <span>Build your brand with a verified profile</span>
                                </div>
                                <div className="tcta-perk">
                                    <i className="bi bi-check-circle-fill"></i>
                                    <span>Grow your income with flexible scheduling</span>
                                </div>
                            </div>
                            <div className="tcta-actions">
                                <Link to="/create-fitness-trainer-profile" className="btn-tcta-primary">
                                    <i className="bi bi-person-plus-fill me-2"></i>Register as Trainer — It's Free
                                </Link>
                                <Link to="/gym-fitness-trainer/all" className="btn-tcta-ghost">
                                    Browse Trainers <i className="bi bi-arrow-right ms-1"></i>
                                </Link>
                            </div>
                        </div>

                        {/* Right: Stats cards */}
                        <div className="tcta-right">
                            <div className="tcta-stats-grid">
                                <div className="tcta-stat-card">
                                    <div className="tcta-stat-icon">
                                        <i className="bi bi-people-fill"></i>
                                    </div>
                                    <div className="tcta-stat-num">10,000+</div>
                                    <div className="tcta-stat-label">Active Trainers</div>
                                </div>
                                <div className="tcta-stat-card accent">
                                    <div className="tcta-stat-icon">
                                        <i className="bi bi-graph-up-arrow"></i>
                                    </div>
                                    <div className="tcta-stat-num">50+</div>
                                    <div className="tcta-stat-label">Cities Covered</div>
                                </div>
                                <div className="tcta-stat-card accent">
                                    <div className="tcta-stat-icon">
                                        <i className="bi bi-currency-rupee"></i>
                                    </div>
                                    <div className="tcta-stat-num">₹0</div>
                                    <div className="tcta-stat-label">Registration Fee</div>
                                </div>
                                <div className="tcta-stat-card">
                                    <div className="tcta-stat-icon">
                                        <i className="bi bi-star-fill"></i>
                                    </div>
                                    <div className="tcta-stat-num">4.8★</div>
                                    <div className="tcta-stat-label">Avg. Trainer Rating</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* J. Blog Section */}
            <section className="blog-section-home">
                <div className="container">
                    <div className="section-header-row">
                        <div>
                            <h2 className="section-title-left">Fitness Blog</h2>
                            <p className="section-subtitle-left">Latest tips, guides, and insights</p>
                        </div>
                        <Link to="/blog" className="see-all-link">
                            View All <i className="bi bi-arrow-right-circle-fill ms-2"></i>
                        </Link>
                    </div>

                    <div className="blog-grid">
                        {[
                            { title: "10 Best Exercises for Weight Loss", category: "Training Tips", image: placeimage, excerpt: "Discover the most effective exercises to burn fat and lose weight quickly..." },
                            { title: "Complete Guide to Muscle Building Diet", category: "Nutrition", image: placeimage, excerpt: "Learn what to eat to build lean muscle mass and improve your physique..." },
                            { title: "Morning Workout Routine for Beginners", category: "Workout Plans", image: placeimage, excerpt: "Start your day right with this simple yet effective morning workout routine..." },
                            { title: "Benefits of Hiring a Personal Trainer", category: "Fitness Industry", image: placeimage, excerpt: "Why investing in a personal trainer is the best decision for your fitness..." }
                        ].map((blog, index) => (
                            <Link key={index} to="/blog" className="blog-card-link">
                                <div className="blog-card-home">
                                    <div className="blog-image-wrapper">
                                        <img src={blog.image} alt={blog.title} onError={imageOnError} className="blog-card-image" />
                                        <span className="blog-category-badge">{blog.category}</span>
                                    </div>
                                    <div className="blog-card-content">
                                        <h3>{blog.title}</h3>
                                        <p>{blog.excerpt}</p>
                                        <div className="blog-read-more">Read More <i className="bi bi-arrow-right"></i></div>
                                    </div>
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            <Faq page="fitness_trainer" />
        </>
    );
}
